/* Module-level ref-counted `inert` registry for Modal siblings.
   Rationale: per-modal snapshotting doesn't survive stacked modals.
   Scenario:
     1. body has <header> + <main>.
     2. First modal opens → marks header+main inert; tracks
        [header, main] for its own cleanup.
     3. Second modal opens → walks body children, finds header +
        main already inert, skips them; only adds first's overlay
        to its own tracking.
     4. First modal closes first → its cleanup clears inert on
        header+main.
     5. Second modal is still open but header+main are no longer
        inert — the outer page is AT-reachable again while a
        modal is still up.

   A ref-counted registry fixes it: each modal acquires inert on
   every non-modal child of body; the child stays inert until the
   last outstanding modal releases it. Identical to how scrollLock
   handles depth across modals. */

interface InertEntry {
  count: number;
  /* Was inert already set by code outside the kit before we
     first acquired? If so, the final release DOESN'T clear it. */
  preExisting: boolean;
}

const _inertRegistry = new WeakMap<HTMLElement, InertEntry>();

export function acquireSiblingInert(excludeOverlay: HTMLElement | null): HTMLElement[] {
  if (typeof document === 'undefined') return [];
  const acquired: HTMLElement[] = [];
  for (const child of Array.from(document.body.children) as HTMLElement[]) {
    if (child === excludeOverlay) continue;
    let entry = _inertRegistry.get(child);
    if (!entry) {
      entry = { count: 0, preExisting: child.hasAttribute('inert') };
      _inertRegistry.set(child, entry);
    }
    entry.count += 1;
    if (!child.hasAttribute('inert')) child.setAttribute('inert', '');
    acquired.push(child);
  }
  return acquired;
}

export function releaseSiblingInert(acquired: HTMLElement[]): void {
  if (typeof document === 'undefined') return;
  for (const child of acquired) {
    const entry = _inertRegistry.get(child);
    if (!entry) continue;
    entry.count -= 1;
    if (entry.count <= 0) {
      if (!entry.preExisting) child.removeAttribute('inert');
      _inertRegistry.delete(child);
    }
  }
}
